import { useMemo } from "react";

/**
 * Extract index name from symbol
 * @param {string} symbol - e.g., "NSE:BANKNIFTY25NOV58000PE"
 * @returns {string|null} - e.g., "BANKNIFTY" or null for non-index symbols
 */
function getIndexFromSymbol(symbol) {
  try {
    if (!symbol) return null;
    const symbolUpper = symbol.toUpperCase();

    // Remove NSE: prefix if exists and normalize to uppercase
    const cleanSymbol = symbolUpper.replace("NSE:", "");

    // Exclude MCX, NFO, BFO symbols, and all FUT symbols - don't track them in OrdersView
    if (symbolUpper.includes("MCX") || symbolUpper.includes("NFO") || symbolUpper.includes("BFO") || symbolUpper.endsWith("FUT")) return null;

    // Check for longer index names first to avoid false matches
    if (cleanSymbol.includes("BANKNIFTY")) return "BANKNIFTY";
    if (cleanSymbol.includes("FINNIFTY") || cleanSymbol.includes("FIN NIFTY"))
      return "FINNIFTY";
    if (cleanSymbol.includes("MIDCPNIFTY") || cleanSymbol.includes("MIDCP"))
      return "MIDCPNIFTY";
    if (cleanSymbol.includes("SENSEX") || cleanSymbol.includes("BSX"))
      return "SENSEX";
    if (cleanSymbol.includes("NIFTY")) return "NIFTY";

    // Not an index - return null (ignore stocks)
    return null;
  } catch (e) {
    console.error("Error extracting index from symbol:", symbol, e);
    return null;
  }
}

/**
 * Normalize order/position side to 'buy' | 'sell' | null
 * Accepts numeric (1,2,-1), strings ('1','2','-1','BUY','SELL') and returns 'buy' or 'sell'
 */
function normalizeSide(rawSide) {
  if (rawSide === null || rawSide === undefined) return null;
  const s = String(rawSide).trim().toLowerCase();
  if (s === "1" || s === "buy" || s === "b") return "buy";
  if (s === "2" || s === "-1" || s === "sell" || s === "s") return "sell";
  return null;
}

/**
 * Calculate MODE (most frequent value) from an array of numbers
 * @param {number[]} numbers
 * @returns {number}
 */
function calculateMode(numbers) {
  if (!numbers || numbers.length === 0) return 0;

  const frequency = {};
  let maxFreq = 0;
  let mode = numbers[0];

  numbers.forEach((num) => {
    frequency[num] = (frequency[num] || 0) + 1;
    if (frequency[num] > maxFreq) {
      maxFreq = frequency[num];
      mode = num;
    }
  });

  return mode;
}

/**
 * Calculate base quantity for each unique symbol+strategy combination (for OrderComparisonModal)
 * @param {Array} orders - Orders data
 * @returns {Object} - { "symbol_strategy": baseQty, ... }
 */
function calculateSymbolStrategyBaseQtyFromOrders(orders) {
  const symbolStrategyQtyMap = {};

  // Filter only BUY orders (side === 1) - match client-old logic exactly
  const buyOrders = orders.filter((order) => order.side === 1);

  buyOrders.forEach((order) => {
    const index = getIndexFromSymbol(order.symbol);

    // Skip non-index orders
    if (!index) return;

    const key = `${order.symbol}_${order.strategy || "NO_STRATEGY"}`;

    if (!symbolStrategyQtyMap[key]) {
      symbolStrategyQtyMap[key] = {
        symbol: order.symbol,
        strategy: order.strategy,
        index,
        clientQuantities: {},
      };
    }

    // Sum quantities by client_id
    const clientId = order.clientId || order.client_id;
    const qty = Math.abs(parseFloat(order.qty || 0));

    if (!symbolStrategyQtyMap[key].clientQuantities[clientId]) {
      symbolStrategyQtyMap[key].clientQuantities[clientId] = 0;
    }
    symbolStrategyQtyMap[key].clientQuantities[clientId] += qty;
  });

  // Calculate MODE for each symbol+strategy combination
  const baseQtyMap = {};
  Object.entries(symbolStrategyQtyMap).forEach(([key, data]) => {
    const quantities = Object.values(data.clientQuantities);
    const mode = calculateMode(quantities);
    baseQtyMap[key] = {
      symbol: data.symbol,
      strategy: data.strategy,
      index: data.index,
      baseQty: mode,
      clients: Object.keys(data.clientQuantities).length,
      allQtys: quantities,
    };
  });

  return baseQtyMap;
}

/**
 * Group and sum buy orders by client and index
 * @param {Array} orders - Orders data
 * @returns {Object} - Grouped buy orders
 */
function groupBuyOrdersByClientIndex(orders) {
  const grouped = {};

  // Debug: Log all orders first
  // console.log("🔍 [DEBUG] Raw orders received:", orders.length);

  // Check a few sample orders to see their structure
  if (orders.length > 0) {
    // console.log("📋 [DEBUG] Sample raw orders:");
    orders.slice(0, 3).forEach((order, idx) => {
      // console.log(
      //   `  ${idx + 1}. ${order.client_id} | ${order.symbol} | side: ${
      //     order.side
      //   } | status_no: ${order.status_no} | qty: ${order.qty}`
      // );
    });
  }

  // Filter only BUY orders (side === 1) - match client-old logic exactly
  const buyOrders = orders.filter((order) => order.side === 1);

  // console.log(
  //   `🔍 [DEBUG] Filtered ${buyOrders.length} buy orders from ${orders.length} total orders`
  // );

  // Debug: Show buy orders by client
  const clientOrderCounts = {};
  buyOrders.forEach((order) => {
    const clientId = order.clientId || order.client_id;
    if (!clientOrderCounts[clientId]) clientOrderCounts[clientId] = 0;
    clientOrderCounts[clientId]++;
  });

  //console.log("📊 [DEBUG] Buy orders per client:");
  Object.entries(clientOrderCounts).forEach(([clientId, count]) => {
    //  console.log(`  ${clientId}: ${count} buy orders`);
  });

  buyOrders.forEach((order) => {
    const index = getIndexFromSymbol(order.symbol);

    // Skip MCX or null indexes
    if (!index || index === null) {
      return;
    }

    // Handle both clientId (camelCase) and client_id (snake_case)
    const clientId = order.clientId || order.client_id;
    const key = `${clientId}_${index}`;

    if (!grouped[key]) {
      grouped[key] = {
        client_id: clientId,
        index,
        qty: 0,
        orders: [],
      };
    }

    grouped[key].qty += order.qty || 0; // Match client-old logic exactly
    grouped[key].orders.push(order);

    // Debug logging for HV00061 NIFTY orders
    if (clientId === "HV00061" && index === "NIFTY") {
      // console.log(`🔍 [NIFTY DEBUG] Adding order for HV00061:`, {
      //   order_id: order.order_id,
      //   symbol: order.symbol,
      //   strategy: order.strategy,
      //   qty: order.qty,
      //   running_total: grouped[key].qty,
      //   order_count: grouped[key].orders.length,
      // });
    }
  });

  // Debug: Log final grouped data for HV00061
  Object.entries(grouped).forEach(([key, group]) => {
    if (group.client_id === "HV00061") {

    }
  });

  return grouped;
}

/**
 * Calculate base quantity (MODE) for each index from buy orders
 * This uses the same logic as positions: group by client+index, then find MODE
 * @param {Object} groupedOrders
 * @returns {Object} - { NIFTY: 100, BANKNIFTY: 150, ... }
 */
function calculateBaseQtyMapFromOrders(groupedOrders) {
  const indexQuantities = {};

  // Group quantities by index
  Object.values(groupedOrders).forEach((group) => {
    if (!indexQuantities[group.index]) {
      indexQuantities[group.index] = [];
    }
    indexQuantities[group.index].push(group.qty);
  });

  // Calculate MODE for each index
  const baseQtyMap = {};
  Object.keys(indexQuantities).forEach((index) => {
    const mode = calculateMode(indexQuantities[index]);
    baseQtyMap[index] = mode;

    // Debug: Log base qty calculation
    // console.log(
    //   `📐 [BASE QTY] ${index}: MODE=${mode} from quantities:`,
    //   indexQuantities[index].sort((a, b) => a - b)
    // );
  });

  return baseQtyMap;
}

/**
 * Determine color based on validation rules for orders
 * @param {number} actualQty - Actual order quantity
 * @param {number} expectedQty - Expected quantity (multiplier × base qty)
 * @param {boolean} hasOrders - Whether user has placed orders
 * @returns {string} - 'green' or 'red'
 */
function determineOrderColor(actualQty, expectedQty, hasOrders) {
  // No orders placed - red
  if (!hasOrders) {
    return "red";
  }

  // Has orders but quantity doesn't match - red
  if (actualQty !== expectedQty) {
    return "red";
  }

  // Has orders and quantity matches - green
  return "green";
}

/**
 * Hook for Orders View data processing
 * @param {Array} orders - Orders data from API
 * @param {Array} userMappings - User mapping data with multipliers
 * @param {number} ordersUpdateVersion - Version counter to force re-computation
 * @param {Array} users - Global users array fetched from backend containing full details like is_pledge_plus
 * @returns {Object} - { data, baseQtyMap, activeIndexes }
 */
export function useOrdersView(orders, userMappings, ordersUpdateVersion, users) {
  const orderViewData = useMemo(() => {
    // ---------------------------------------------
    // ✅ Buy Orders Count (Same as DashboardNew)
    // ---------------------------------------------
    //   console.log("Orders HERE-----",orders);
    //   const buyOrdersCount =
    //  orders.filter(
    //   (o) =>
    //     o.side === 1 &&
    //     (String(o.status_no) === "6" || String(o.status_no) === "4")
    // ).length;

    // When no orders exist, still build allClientsData from userMappings
    // so "ALL CLIENTS" view shows all active clients (all marked as missed)
    if (!orders || orders.length === 0) {
      if (!userMappings || userMappings.length === 0) {
        return {
          data: [],
          allClientsData: [],
          baseQtyMap: {},
          activeIndexes: [],
          symbolStrategyBaseQty: {},
          buyOrdersCount: 0,
        };
      }

      const allClientsData = [];
      const indexes = ["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY", "SENSEX"];
      const clientBrokerMap = {};
      userMappings.forEach(u => {
        if (!clientBrokerMap[u.client_id]) clientBrokerMap[u.client_id] = new Set();
        clientBrokerMap[u.client_id].add((u.broker || "").toLowerCase());
      });

      userMappings.forEach((userMapping) => {
        if (userMapping.status !== "active") return;
        const clientId = userMapping.client_id;
        const matchedUser = users?.find(u => u.client_id === clientId || u.clientId === clientId);
        const isPledgePlus = matchedUser?.is_pledge_plus || userMapping.is_pledge_plus || matchedUser?.is_pledgeplus_mini || userMapping.is_pledgeplus_mini;
        if (isPledgePlus === true || String(isPledgePlus).toLowerCase() === "true") {
          const hasOptimusMultiplier =
            parseFloat(userMapping.multiplier) >= 1 ||
            parseFloat(userMapping.banknifty_multiplier) >= 1 ||
            parseFloat(userMapping.finnifty_multiplier) >= 1 ||
            parseFloat(userMapping.midcpnifty_multiplier) >= 1 ||
            parseFloat(userMapping.sensex_multiplier) >= 1;
          if (!hasOptimusMultiplier) return;
        }

        const broker = (userMapping.broker || "").toLowerCase();

        // Exclude Zerodha clients from Optimus Order View
        if (broker === "zerodha") return;

        const hasXts = clientBrokerMap[clientId]?.has("dhan_xts");
        const isSs = broker === "dhan_ss";
        if (hasXts && isSs) return;

        const rowData = {
          clientId,
          clientName: userMapping.name,
          broker: userMapping.broker,
          indexCount: 0,
          hasOrdersCount: 0,
        };

        let hasAnyMultiplier = false;
        indexes.forEach((index) => {
          let key = index === "NIFTY" ? "multiplier" : `${index.toLowerCase()}_multiplier`;
          const multiplier = parseFloat(userMapping[key]) || 0;
          if (multiplier < 1) return;
          hasAnyMultiplier = true;
          rowData[index.toLowerCase()] = {
            multiplier,
            qty: 0,
            color: "red",
            expectedQty: 0,
            hasOrders: false,
          };
        });

        if (hasAnyMultiplier) allClientsData.push(rowData);
      });

      // Derive activeIndexes from whichever index keys are present in allClientsData
      const indexKeys = ["nifty", "banknifty", "finnifty", "midcpnifty", "sensex"];
      const activeIndexes = indexKeys.filter(i => allClientsData.some(row => row[i] !== undefined));

      return {
        data: [],
        allClientsData,
        baseQtyMap: {},
        activeIndexes,
        symbolStrategyBaseQty: {},
        buyOrdersCount: 0,
      };
    }

    // Step 1: Group buy orders
    const groupedBuyOrders = groupBuyOrdersByClientIndex(orders);

    // Step 1.5: Symbol+Strategy base qty
    const symbolStrategyBaseQty =
      calculateSymbolStrategyBaseQtyFromOrders(orders);

    // Step 2: Base Qty Mode
    const baseQtyMap = calculateBaseQtyMapFromOrders(groupedBuyOrders);

    // Step 3: Client → Index aggregation
    const clientIndexMap = {};


    // Step 0: Buy Order Count (side=1 AND status 4 or 6 AND index-only)
    const buyOrderCount = orders.filter((o) => {
      const isBuy = o.side === 1;
      const isValidStatus =
        String(o.status_no) === "6" || String(o.status_no) === "4";

      const index = getIndexFromSymbol(o.symbol); // null = NON INDEX

      return isBuy && isValidStatus && index;
    }).length;


    Object.values(groupedBuyOrders).forEach((orderGroup) => {
      const clientId = orderGroup.client_id;
      const index = orderGroup.index;

      if (!clientIndexMap[clientId]) clientIndexMap[clientId] = {};
      if (!clientIndexMap[clientId][index]) {
        clientIndexMap[clientId][index] = { qty: 0 };
      }

      clientIndexMap[clientId][index].qty += orderGroup.qty;
    });

    // Step 4: Prepare table rows
    const data = [];
    const allClientsData = [];

    const indexesWithOrders = new Set();
    Object.values(clientIndexMap).forEach((clientData) => {
      Object.keys(clientData).forEach((index) =>
        indexesWithOrders.add(index)
      );
    });

    // ---------------------------------------------
    // CASE 1: userMappings not loaded
    // ---------------------------------------------
    if (!userMappings || userMappings.length === 0) {
      Object.keys(clientIndexMap).forEach((clientId) => {
        const rowData = {
          clientId,
          clientName: `Client ${clientId}`,
          broker: "unknown",
          indexCount: 0,
          hasOrdersCount: 0,
        };

        const indexes = [
          "NIFTY",
          "BANKNIFTY",
          "FINNIFTY",
          "MIDCPNIFTY",
          "SENSEX",
        ];

        let hasAnyData = false;

        indexes.forEach((index) => {
          if (!indexesWithOrders.has(index)) return;

          const indexData = clientIndexMap[clientId][index];
          if (indexData) {
            hasAnyData = true;
            rowData.indexCount++;
            rowData.hasOrdersCount++;
            rowData[index] = {
              multiplier: 0,
              qty: indexData.qty,
              color: "red",
              expectedQty: 0,
              hasOrders: true,
            };
          }
        });

        if (hasAnyData) {
          data.push(rowData);
          allClientsData.push(rowData);
        }
      });

      return {
        data,
        allClientsData,
        baseQtyMap,
        activeIndexes: Array.from(indexesWithOrders),
        symbolStrategyBaseQty,
        buyOrdersCount: data.length, // RETURNED
      };
    }

    // ---------------------------------------------
    // CASE 2: userMappings available - full validation
    // ---------------------------------------------
    // Pre-calculate broker map to identify duplicates
    const clientBrokerMap = {};
    if (userMappings) {
      userMappings.forEach(u => {
        if (!clientBrokerMap[u.client_id]) clientBrokerMap[u.client_id] = new Set();
        clientBrokerMap[u.client_id].add((u.broker || "").toLowerCase());
      });
    }

    userMappings.forEach((userMapping) => {
      if (userMapping.status !== "active") return;

      const clientId = userMapping.client_id;

      // Look up full user details from the /get-fyers-users API array
      const matchedUser = users?.find(u => u.client_id === clientId || u.clientId === clientId);
      const isPledgePlus = matchedUser?.is_pledge_plus || userMapping.is_pledge_plus || matchedUser?.is_pledgeplus_mini || userMapping.is_pledgeplus_mini;

      // Exclude Pledge Plus users from Optimus Order View ONLY if they have no Optimus multipliers.
      // Some clients are in both Pledge Plus and Optimus — if they have multipliers set, include them here too.
      if (isPledgePlus === true || String(isPledgePlus).toLowerCase() === "true") {
        const hasOptimusMultiplier =
          parseFloat(userMapping.multiplier) >= 1 ||
          parseFloat(userMapping.banknifty_multiplier) >= 1 ||
          parseFloat(userMapping.finnifty_multiplier) >= 1 ||
          parseFloat(userMapping.midcpnifty_multiplier) >= 1 ||
          parseFloat(userMapping.sensex_multiplier) >= 1;
        if (!hasOptimusMultiplier) return;
      }

      const broker = (userMapping.broker || "").toLowerCase();

      // Exclude Zerodha clients from Optimus Order View
      if (broker === "zerodha") return;

      const hasXts = clientBrokerMap[clientId]?.has("dhan_xts");
      const isSs = broker === "dhan_ss";

      // If we are processing SS row, but XTS exists, we SKIP looking for data.
      const shouldSkipData = hasXts && isSs;

      const rowData = {
        clientId,
        clientName: userMapping.name,
        broker: userMapping.broker,
        indexCount: 0,
        hasOrdersCount: 0,
      };

      const indexes = [
        "NIFTY",
        "BANKNIFTY",
        "FINNIFTY",
        "MIDCPNIFTY",
        "SENSEX",
      ];

      let hasAnyData = false;
      let hasAnyMultiplier = false;

      indexes.forEach((index) => {
        if (shouldSkipData) return;

        let key =
          index === "NIFTY"
            ? "multiplier"
            : `${index.toLowerCase()}_multiplier`;

        const multiplier = parseFloat(userMapping[key]) || 0;
        if (multiplier < 1) return;

        hasAnyMultiplier = true;

        const baseQty = baseQtyMap[index] || 0;
        const expectedQty = multiplier * baseQty;

        // For the normal view (data), only include indexes that have at least one buy order
        const isIndexActive = indexesWithOrders.has(index);

        if (isIndexActive && clientIndexMap[clientId]?.[index]) {
          const idxData = clientIndexMap[clientId][index];
          const color = determineOrderColor(idxData.qty, expectedQty, true);

          hasAnyData = true;
          rowData.indexCount++;

          rowData[index.toLowerCase()] = {
            multiplier,
            qty: idxData.qty,
            color,
            expectedQty,
            hasOrders: true,
          };

          rowData.hasOrdersCount++;
        } else {
          // No orders for this index (either index has no orders at all, or this client has none)
          // Still include in allClientsData as "missed"
          rowData[index.toLowerCase()] = {
            multiplier,
            qty: 0,
            color: "red",
            expectedQty,
            hasOrders: false,
          };
        }
      });

      if (hasAnyData) data.push(rowData);
      if (hasAnyMultiplier) allClientsData.push(rowData);
    });

    // Sorting
    data.sort((a, b) => {
      if (a.hasOrdersCount !== b.hasOrdersCount)
        return b.hasOrdersCount - a.hasOrdersCount;
      return b.indexCount - a.indexCount;
    });

    allClientsData.sort((a, b) => {
      if (a.hasOrdersCount !== b.hasOrdersCount)
        return b.hasOrdersCount - a.hasOrdersCount;
      return b.indexCount - a.indexCount;
    });

    const indexKeys = [
      "nifty",
      "banknifty",
      "finnifty",
      "midcpnifty",
      "sensex",
    ];

    // Normal mode (data): only show columns for indexes where pending orders actually exist
    // All Clients mode (allClientsData): show all columns based on multipliers
    const activeIndexes = indexKeys.filter((i) =>
      data.some((row) => row[i] !== undefined && row[i]?.hasOrders === true)
    );

    const allClientsActiveIndexes = indexKeys.filter((i) =>
      allClientsData.some((row) => row[i] !== undefined)
    );

    return {
      data,
      allClientsData,
      baseQtyMap,
      activeIndexes,
      allClientsActiveIndexes,
      symbolStrategyBaseQty,
      buyOrdersCount: buyOrderCount, // ✅ ALWAYS RETURN THIS
    };
  }, [orders, userMappings, ordersUpdateVersion, users]);

  return orderViewData;
}
